import { useCallback, useEffect, useState } from 'react';
import locationService from '../services/locationService';
import requestService from '../services/requestService';
import {
    extractApiArray,
    getInitialMotoSearchForm,
    normalizeOptionItems,
} from '../utils/requestHelpers';

const useMotoSearch = ({ show, requestId, request }) => {
    const [form, setForm] = useState(getInitialMotoSearchForm(request));

    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [locations, setLocations] = useState([]);

    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);
    const [loadingLocations, setLoadingLocations] = useState(false);

    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResult, setSearchResult] = useState(null);

    const [errorMsg, setErrorMsg] = useState('');

    const updateField = useCallback((key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    }, []);

    const loadCities = useCallback(async (countryId) => {
        if (!countryId) {
            setCities([]);
            return;
        }

        setLoadingCities(true);

        try {
            const response = await locationService.getCities(countryId);

            setCities(
                normalizeOptionItems(
                    extractApiArray(response)
                )
            );
        } catch (e) {
            setErrorMsg(
                e?.response?.data?.message ||
                e?.message ||
                'Failed to load cities'
            );

            setCities([]);
        } finally {
            setLoadingCities(false);
        }
    }, []);

    const loadCountries = useCallback(async () => {
        setLoadingCountries(true);

        try {
            const response = await locationService.getCountries();

            const countryOptions = normalizeOptionItems(
                extractApiArray(response)
            );

            setCountries(countryOptions);

            // Request reference:
            // request.contactCountry = "JP"
            if (request?.contactCountry) {
                const target = String(request.contactCountry)
                    .trim()
                    .toLowerCase();

                const matchedCountry = countryOptions.find((item) => {
                    const isoCode = String(item?.isoCode || '')
                        .trim()
                        .toLowerCase();

                    const label = String(item?.label || '')
                        .trim()
                        .toLowerCase();

                    return (
                        isoCode === target ||
                        label === target
                    );
                });

                if (matchedCountry) {
                    setForm((prev) => ({
                        ...prev,
                        countryId: matchedCountry.value,
                        cityId: '',
                        locationId: '',
                    }));

                    // 加载日本对应的 City 列表
                    // 但不默认选择 City
                    await loadCities(matchedCountry.value);
                }
            }
        } catch (e) {
            setErrorMsg(
                e?.response?.data?.message ||
                e?.message ||
                'Failed to load countries'
            );

            setCountries([]);
        } finally {
            setLoadingCountries(false);
        }
    }, [request?.contactCountry, loadCities]);

    const loadLocations = useCallback(async (cityId) => {
        if (!cityId) {
            setLocations([]);
            return;
        }

        setLoadingLocations(true);
        try {
            const response = await locationService.getLocations(cityId);
            setLocations(normalizeOptionItems(extractApiArray(response)));
        } catch (e) {
            setErrorMsg(e?.response?.data?.message || e?.message || 'Failed to load locations');
            setLocations([]);
        } finally {
            setLoadingLocations(false);
        }
    }, []);

    useEffect(() => {
        if (!show) return;

        setErrorMsg('');
        setCountries([]);
        setCities([]);
        setLocations([]);
        setSearchResult(null);
        setForm(getInitialMotoSearchForm(request));

        loadCountries();
    }, [show, request, loadCountries]);

    const handleCountryChange = useCallback(async (countryId) => {
        setErrorMsg('');
        setForm((prev) => ({
            ...prev,
            countryId: countryId || '',
            cityId: '',
            locationId: '',
        }));
        setCities([]);
        setLocations([]);
        setSearchResult(null);

        if (!countryId) return;
        await loadCities(countryId);
    }, [loadCities]);

    const handleCityChange = useCallback(async (cityId) => {
        setErrorMsg('');
        setForm((prev) => ({
            ...prev,
            cityId: cityId || '',
            locationId: '',
        }));
        setLocations([]);
        setSearchResult(null);

        if (!cityId) return;
        await loadLocations(cityId);
    }, [loadLocations]);

    const handleSearch = useCallback(async () => {
        try {
            setSearchLoading(true);
            setErrorMsg('');

            const payload = {
                countryId: form.countryId || undefined,
                cityId: form.cityId || undefined,
                locationId: form.locationId || undefined,
                startDate: form.startDate,
                startTime: form.startTime || undefined,
                flexibleStartTime: !!form.flexibleStartTime,
                endDate: form.endDate,
                endTime: form.endTime || undefined,
                flexibleEndTime: !!form.flexibleEndTime,
            };

            const data = await requestService.searchMotos(requestId, payload);
            setSearchResult(data || null);
        } catch (e) {
            setErrorMsg(e?.response?.data?.message || e?.message || 'Failed to search shops');
            setSearchResult(null);
        } finally {
            setSearchLoading(false);
        }
    }, [form, requestId]);

    return {
        form,
        countries,
        cities,
        locations,
        loadingCountries,
        loadingCities,
        loadingLocations,
        searchLoading,
        searchResult,
        errorMsg,
        updateField,
        handleCountryChange,
        handleCityChange,
        handleSearch,
    };
};

export default useMotoSearch;